// Shared rules for how much of a participant's profile is exposed to
// other attendees. The single source of truth so the participants list,
// participant modal, profile page, lobby, etc. all behave identically and
// stay driven by the admin's settings.
//
// Admin inputs:
//   • participantVisibilityMode ('visible' | 'limited' | 'hidden') —
//       the "User profile settings" Visible / Limited-visible / Not-visible
//       toggle. 'hidden' means the list isn't shown at all.
//   • features.profileEmailVisible / profilePhoneVisible — whether the
//       email / phone field may be shown at all.
//   • participant.EmailAndPhoneVisible — the participant's own opt-in,
//       which only matters in 'limited' mode.

export default {
  methods: {
    // Getters read directly (not as mapped computeds) so this mixin can
    // be used by components that already map `features` /
    // `participantVisibilityMode` without colliding (vue/no-dupe-keys).
    _pvMode() {
      try {
        return this.$store.getters['CurrentEvent/participantVisibilityMode'];
      } catch (e) {
        return 'hidden';
      }
    },
    _pvFeatures() {
      try {
        return this.$store.getters['CurrentEvent/features'] || {};
      } catch (e) {
        return {};
      }
    },
    // Whether ANY contact detail may be shown for this participant.
    //   'visible' → always shown.
    //   'limited' → shown only if the participant opted in.
    //   'hidden'  → never.
    participantContactVisible(p) {
      if (!p) return false;
      const mode = this._pvMode();
      if (mode === 'hidden') return false;
      if (mode === 'limited') return p.EmailAndPhoneVisible === true;
      // 'visible' — always shown; the participant's own choice only
      // matters in 'limited' mode.
      return true;
    },
    participantEmail(p) {
      return (p && (p.Email || p.email)) || '';
    },
    // Phone lives under different keys across payloads: own profile uses
    // `Cell`, attendee records use `Phone` or `Phonenumber`.
    participantPhone(p) {
      return (
        (p && (p.Cell || p.Phone || p.Phonenumber || p.cell || p.phone)) || ''
      );
    },
    participantEmailVisible(p) {
      const f = this._pvFeatures();
      return (
        !!f.profileEmailVisible &&
        this.participantContactVisible(p) &&
        !!this.participantEmail(p)
      );
    },
    participantPhoneVisible(p) {
      const f = this._pvFeatures();
      return (
        !!f.profilePhoneVisible &&
        this.participantContactVisible(p) &&
        !!this.participantPhone(p)
      );
    },
    // Convenience: job title / company are profile (non-contact) fields —
    // shown whenever the participant list itself is shown.
    participantHasContactDetails(p) {
      return this.participantEmailVisible(p) || this.participantPhoneVisible(p);
    }
  }
};
